import type { Message } from '@ag-ui/client';
import { AG_UI_ROLE } from './agent/ag-ui.constants';
import type { PandaHttpAgent } from './agent/panda-agent';
import type { SearchAnswerStatus } from './search-answer/search-answer.constants';

/**
 * The search card's handoff to the chat, off Redux: the run's messages mutate
 * token by token (mirroring them into the store trips react-redux's serialize
 * check and OOMs), and a live agent isn't serialisable anyway. Redux carries
 * only the handoff id; the payload — the exchange so far, plus the live run
 * when the answer is still streaming — travels here. One slot: the newest
 * handoff wins. Taking is idempotent per id so a remounted chat can
 * re-adopt; a different id still returns null, so a stale entry can't be
 * adopted.
 */
export function toHandoffMessages(messages: readonly Message[]): Message[] {
  return messages.filter(
    message => message.role === AG_UI_ROLE.USER || message.role === AG_UI_ROLE.ASSISTANT,
  );
}

export interface LiveHandoff {
  agent: PandaHttpAgent;
  /** Resolves with the status the run ended in. Never rejects. */
  settled: Promise<SearchAnswerStatus>;
  /**
   * Per-token message changes. The chat re-renders off this while it displays
   * the adopted run — AG-UI won't notify a subscriber added after the run
   * started. Returns an unsubscribe.
   */
  subscribe: (listener: () => void) => () => void;
  abort: () => void;
}

export type Handoff = (
  | { kind: 'live'; live: LiveHandoff }
  | {
      kind: 'complete';
      messages: Message[];
      /** The chat auto-runs the exchange's trailing hub offer on adoption. */
      acceptOffer?: boolean;
    }
) & { parentRunId?: string };

let pending: { id: string; handoff: Handoff } | null = null;

export function stashHandoff(id: string, handoff: Handoff): void {
  // A displaced entry is dead either way — the chat adopts the new one and
  // resets — so stop its stream instead of letting it run headless.
  if (pending?.handoff.kind === 'live') pending.handoff.live.abort();
  pending = { id, handoff };
}

export function takeHandoff(id: string): Handoff | null {
  if (pending?.id !== id) return null;
  return pending.handoff;
}
